import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect } from "react";
import { FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";

import { useSyncedRef } from "../../hooks/useSyncedRef";
import { FormItem } from "./FormItem";

import type { ComponentPropsWithoutRef, ForwardedRef, ReactElement, ReactNode } from "react";
import type {
  DefaultValues,
  FieldArrayPath,
  FieldValues,
  UseFieldArrayProps,
  UseFieldArrayReturn,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form";
import type { z, ZodType } from "zod";

export type { FormItemProps } from "./FormItem";

export interface FormProps<T extends ZodType<FieldValues, FieldValues>>
  extends Omit<ComponentPropsWithoutRef<"form">, "onSubmit">,
    UseFormProps<z.TypeOf<T>> {
  /** Zod schema to use for validating the form. */
  schema: T;
  onSubmit: (values: z.TypeOf<T>) => void;
  /**
   * Whether the form state will automatically reset after a completed submission. Use this if
   * you want the user to stay on the page and potentially submit the form again.
   */
  resetAfterSubmit?: boolean;
  /** Values to set in the form fields after resetting the form. */
  resetValues?: DefaultValues<z.TypeOf<T>>;
}

function FormImpl<T extends ZodType<FieldValues, FieldValues>>(
  {
    children,
    schema,
    onSubmit,
    resetAfterSubmit = false,
    resetValues: resetValuesProp,
    resolver,
    mode = "onTouched",
    disabled,
    reValidateMode,
    defaultValues,
    values,
    errors,
    resetOptions,
    context,
    shouldFocusError,
    shouldUnregister,
    shouldUseNativeValidation,
    progressive,
    criteriaMode,
    delayError,
    ...props
  }: FormProps<T>,
  ref: ForwardedRef<HTMLFormElement>
) {
  const form = useForm<z.infer<T>>({
    resolver: resolver ?? zodResolver(schema),
    mode,
    disabled,
    reValidateMode,
    defaultValues,
    values,
    errors,
    resetOptions,
    context,
    shouldFocusError,
    shouldUnregister,
    shouldUseNativeValidation,
    progressive,
    criteriaMode,
    delayError,
  });

  const { formState, reset } = form;

  // Wrapping this in a ref to avoid re-invoking the effect below
  const resetValues = useSyncedRef(resetValuesProp);

  useEffect(() => {
    if (formState.isSubmitSuccessful && resetAfterSubmit) {
      reset(resetValues.current);
    }
  }, [formState, reset, resetAfterSubmit, resetValues]);

  return (
    <form {...props} onSubmit={form.handleSubmit(onSubmit)} ref={ref}>
      <FormProvider {...form}>{children}</FormProvider>
    </form>
  );
}

const WrappedForm = forwardRef(FormImpl) as <T extends ZodType<FieldValues, FieldValues>>(
  props: FormProps<T> & { ref?: ForwardedRef<HTMLFormElement> }
) => ReactElement<unknown>;

/**
 * Props for Form.List component
 */
export interface FormListProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> extends Omit<UseFieldArrayProps<TFieldValues, TFieldArrayName>, "control"> {
  /** Render function that receives field array methods */
  children: (
    fields: UseFieldArrayReturn<TFieldValues, TFieldArrayName>["fields"],
    operations: {
      add: (value?: unknown, index?: number) => void;
      remove: (index?: number | number[]) => void;
      move: (from: number, to: number) => void;
      insert: (index: number, value?: unknown) => void;
      swap: (indexA: number, indexB: number) => void;
      prepend: (value?: unknown) => void;
      update: (index: number, value: unknown) => void;
      replace: (values: unknown[]) => void;
    }
  ) => ReactNode;
}

/**
 * Form.List provides dynamic field array functionality similar to antd Form.List.
 * It wraps react-hook-form's useFieldArray hook.
 *
 * @example
 * ```tsx
 * <Form.List name="users">
 *   {(fields, { add, remove }) => (
 *     <>
 *       {fields.map((field, index) => (
 *         <Form.Item key={field.id} name={`users.${index}.name`} label="Name">
 *           <Input />
 *         </Form.Item>
 *       ))}
 *       <Button onClick={() => add()}>Add User</Button>
 *     </>
 *   )}
 * </Form.List>
 * ```
 */
function FormList<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
>({
  name,
  children,
  rules,
  shouldUnregister,
  keyName,
}: FormListProps<TFieldValues, TFieldArrayName>) {
  const { control } = useFormContext<TFieldValues>();

  const { fields, append, prepend, remove, swap, move, insert, update, replace } = useFieldArray<
    TFieldValues,
    TFieldArrayName
  >({
    control,
    name,
    rules,
    shouldUnregister,
    keyName,
  });

  // Provide antd-compatible operation names
  const operations = {
    add: (value?: unknown, index?: number) => {
      if (index !== undefined) {
        insert(index, value as TFieldValues[TFieldArrayName][number]);
      } else {
        append(value as TFieldValues[TFieldArrayName][number]);
      }
    },
    remove,
    move,
    insert: (index: number, value?: unknown) =>
      insert(index, value as TFieldValues[TFieldArrayName][number]),
    swap,
    prepend: (value?: unknown) => prepend(value as TFieldValues[TFieldArrayName][number]),
    update: (index: number, value: unknown) =>
      update(index, value as TFieldValues[TFieldArrayName][number]),
    replace: (values: unknown[]) => replace(values as TFieldValues[TFieldArrayName]),
  };

  return <>{children(fields, operations)}</>;
}
FormList.displayName = "Form.List";

/**
 * Hook to get form methods for imperative form control.
 * This is a re-export of react-hook-form's useFormContext.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const form = Form.useFormContext();
 *   // Access form methods: setValue, getValues, reset, etc.
 *   return <Button onClick={() => form.reset()}>Reset</Button>;
 * }
 * ```
 */
function useFormContextWrapper<
  TFieldValues extends FieldValues = FieldValues,
>(): UseFormReturn<TFieldValues> {
  return useFormContext<TFieldValues>();
}

/**
 * Create a form instance for imperative form control.
 * Similar to antd's Form.useForm() hook.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [form] = Form.useForm();
 *   return (
 *     <Form form={form}>...</Form>
 *   );
 * }
 * ```
 * @deprecated Use the Form component's built-in form state instead.
 * For imperative access, use Form.useFormContext() inside a Form.
 */
function useFormHook<TFieldValues extends FieldValues = FieldValues>(
  props?: UseFormProps<TFieldValues>
): [UseFormReturn<TFieldValues>] {
  const form = useForm<TFieldValues>(props);
  return [form];
}

export const Form = WrappedForm as typeof WrappedForm & {
  displayName: string;
  Item: typeof FormItem;
  List: typeof FormList;
  useForm: typeof useFormHook;
  useFormContext: typeof useFormContextWrapper;
};
Form.displayName = "Form";
Form.Item = FormItem;
Form.List = FormList;
Form.useForm = useFormHook;
Form.useFormContext = useFormContextWrapper;
